// OperationReport42Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

// Export libs
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

/* ===== PDF Thai Font helpers ===== */
function base64FromArrayBuffer(buf) { const b = new Uint8Array(buf); const c = 0x8000; let s = ""; for (let i = 0; i < b.length; i += c) { s += String.fromCharCode.apply(null, b.subarray(i, i + c)); } return btoa(s); }
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([fetch("/fonts/Sarabun-Regular.ttf"), fetch("/fonts/Sarabun-Bold.ttf")]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf); const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF(); thaiFontLoaded = true;
}

/* ===== helpers ===== */
const N = v => Number(v ?? 0);
const INT = v => Math.max(0, Math.floor(N(v)));
function fmtMoney(n) { const num = Number(n || 0); return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v;
  const d = typeof v === "string" ? parseISO(v) : v;
  const be = addYears(543, d); const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
function formatMonthYearBE(date) { if (!date) return ""; const be = addYears(543, date); return format(be, "MMMM yyyy", { locale: th }); }

/* ===== Component ===== */
export function OperationReport42Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  // console.log("items", items);
  // เงื่อนไขแสดงคอลัมน์หน่วยงาน
  const showDeptCol =
    String(user?.roldId ?? roleId) === "4" &&
    String(deptIdFilter || "all") === "all";

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map(d => ({ value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""), label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? "") }));
    if (roleId === "4") { setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]); setDeptIdFilter(p => p ?? "all"); }
    else if (roleId === "3") { const self = mapped.find(o => o.value === userDeptId) || { value: userDeptId, label: "หน่วยงานของฉัน" }; setDeptOptions([self]); setDeptIdFilter(userDeptId); }
    else { setDeptOptions(mapped); setDeptIdFilter(p => p || (mapped.find(o => o.value === userDeptId)?.value ?? "")); }
  }, [deptsData, roleId, userDeptId]);
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // โหลดข้อมูลจาก API ใหม่ (SupportOperation42ViewModel)
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation42", {
        method: "POST",
        headers: { token: user.token },
        body: fd
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      // map ฟิลด์ + เติม DeptName + คำนวณต่าง ๆ
      const mapped = raw.map(x => {
        const avgMan = INT(x.avgMan);
        const avg70 = INT(x.avg70);
        const avg80 = INT(avgMan * 0.80);
        const spk = INT(x.spkTrainMan);
        const cnt70to80 = x.cntGt70Lt80 !== undefined ? INT(x.cntGt70Lt80) : Math.max(0, avg80 - avg70);
        const cntOver80 = x.cntGe80 !== undefined ? INT(x.cntGe80) : Math.max(0, Math.min(spk, avgMan) - avg80);
        const amt70 = x.amt70 !== undefined ? N(String(x.amt70).replace(/,/g, "")) : cnt70to80 * 200;
        const amt80 = x.amt80 !== undefined ? N(String(x.amt80).replace(/,/g, "")) : cntOver80 * 300;
        const totalAmt = amt70 + amt80;
        const trainPct = x.trainPercent !== undefined ? Number(x.trainPercent) : (avgMan > 0 ? (spk * 100 / avgMan) : 0);
        const receiptDate = x.receiptDate ?? "-";
        return {
          SupportNo: x.supportNo ?? "",
          SupportName: x.supportName ?? "",
          DeptName: x.deptName ?? x.DeptName ?? "", // <<< เพิ่ม DeptName
          ReceiveYear: x.receiveYear ?? "",
          AvgMan: avgMan,
          SpkTrainMan: spk,
          TrainPercent: Number.isFinite(trainPct) ? trainPct : 0,
          Avg70: avg70,
          Avg80: avg80,
          CntGt70Lt80: cnt70to80,
          CntGe80: cntOver80,
          Amt70: amt70,
          Amt80: amt80,
          TotalAmt: totalAmt,
          Train: INT(x.Train),
          Test: INT(x.Test),
          Oloc: INT(x.Oloc),
          PaymentAmount: x.paymentAmount,
          PaymentDate: x.paymentDate ?? "",
          ReceiptDate: receiptDate,
        };
      });

      setItems(mapped);
    } catch (e) {
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally { setLoading(false); }
  };

  // รวมยอดเงิน "รวม (บาท)"
  const totalPayment = items.reduce((sum, r) => {
    const n = typeof r.PaymentAmount === 'number'
      ? r.PaymentAmount
      : Number(String(r.PaymentAmount ?? 0).replace(/[^\d.-]/g, '')) || 0;
    return sum + n;
  }, 0);

  /* ===== Header spec (3 แถว + merge) แบบ dynamic DeptName ===== */
  const headerRow1 = [
    { content: "ที่", rowSpan: 3 },
    { content: "เลขที่คำขอ", rowSpan: 3 },
    { content: "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
    ...(showDeptCol ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 3 }] : []),
    { content: "ผู้ประกอบกิจการดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้างในปีที่ผ่านมา", colSpan: 9 },
    { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน", colSpan: 3 },
    { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
  ];
  const headerRow2 = [
    { content: "ปี พ.ศ. (ระบุ)", rowSpan: 2 },
    { content: "วันที่ยื่นแบบแสดงการส่งเงินสมทบ", rowSpan: 2 },
    { content: "จำนวนลูกจ้างเฉลี่ย (คน)", rowSpan: 2 },
    { content: "จำนวนลูกจ้างที่ได้รับการพัฒนาฯ (คน)", rowSpan: 2 },
    { content: "ร้อยละของจำนวนลูกจ้างที่ได้รับการพัฒนาฯ", rowSpan: 2 },
    { content: "จำนวนลูกจ้างเฉลี่ย", colSpan: 2 },
    { content: "จำนวนลูกจ้างที่ได้รับการพัฒนาฯ", colSpan: 2 },
    { content: "ส่วนที่เกินกว่า ร้อยละ 70 (บาท)", rowSpan: 2 },
    { content: "ส่วนที่เกินกว่า ร้อยละ 80 (บาท)", rowSpan: 2 },
    { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", rowSpan: 2 },
  ];
  const headerRow3 = [
    "ร้อยละ 70 (คน)", "ร้อยละ 80 (คน)",
    "ส่วนที่เกินกว่า ร้อยละ 70 (คน)", "ส่วนที่เกินกว่า ร้อยละ 80 (คน)",
  ];

  // ดัชนีคอลัมน์ leaf (dynamic)
  const COL = showDeptCol
    ? {
      No: 0, SupportNo: 1, SupportName: 2, DeptName: 3,
      Y: 4,
      RDate: 5,           // ✅ เพิ่ม
      Avg: 6,
      Spk: 7,
      Pct: 8,
      Avg70: 9,
      Avg80: 10,
      Cnt7080: 11,
      Cnt80: 12,
      Amt70: 13,
      Amt80: 14,
      Total: 15,
      PayDate: 16
    }
    : {
      No: 0, SupportNo: 1, SupportName: 2,
      Y: 3,
      RDate: 4,           // ✅ เพิ่ม
      Avg: 5,
      Spk: 6,
      Pct: 7,
      Avg70: 8,
      Avg80: 9,
      Cnt7080: 10,
      Cnt80: 11,
      Amt70: 12,
      Amt80: 13,
      Total: 14,
      PayDate: 15
    };


  /* ===== Export: build AoA + merges (dynamic DeptName) ===== */
  /** ===========================
   *   BUILD HEADER + DATA (Excel)
   *   ใช้ต้นแบบเดียวกับ PDF 100%
   * =========================== */
  const buildAoaAndMerges = () => {

    /** ================================
     * 1) HEADER ROWS (ตาม PDF แบบเป๊ะ)
     * ================================ */

    // จำนวนคอลัมน์ที่ต้องมีทั้งหมด
    const totalCols = showDeptCol ? 17 : 16;

    const row1 = [
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),

      // กลุ่มพัฒนาฝีมือ (9)
      "ผู้ประกอบกิจการดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้างในปีที่ผ่านมา",
      "", "", "", "", "", "", "", "",

      // กลุ่มเงิน (3)
      "จำนวนเงินช่วยเหลือหรืออุดหนุน",
      "", "",

      // วันที่จ่าย
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน"
    ];
    const row2 = [
      "", "", "", ...(showDeptCol ? [""] : []),

      "ปี พ.ศ. (ระบุ)",
      "วัน/เดือน/ปี ที่ยื่นแบบแสดงการส่งเงินสมทบ",
      "จำนวนลูกจ้างเฉลี่ย (คน)",
      "จำนวนลูกจ้างที่ได้รับการพัฒนาฯ (คน)",
      "ร้อยละของจำนวนลูกจ้างที่ได้รับการพัฒนาฯ",

      "จำนวนลูกจ้างเฉลี่ย",
      "",
      "จำนวนลูกจ้างที่ได้รับการพัฒนาฯ",
      "",

      "ส่วนที่เกินกว่า ร้อยละ 70 (บาท)",
      "ส่วนที่เกินกว่า ร้อยละ 80 (บาท)",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",

      ""   // วันที่จ่าย (rowSpan)
    ];



    const row3 = [
      "", "", "", ...(showDeptCol ? [""] : []),

      "", "", "", "", "",

      "ร้อยละ 70 (คน)",
      "ร้อยละ 80 (คน)",
      "ส่วนที่เกินกว่า ร้อยละ 70 (คน)",
      "ส่วนที่เกินกว่า ร้อยละ 80 (คน)",

      "", "", "",
      ""
    ];




    const aoa = [row1, row2, row3];
    const merges = [];

    /** ===========================
     * 2) MERGES (ลอก PDF เป๊ะ)
     * =========================== */

    const fixed = showDeptCol ? 4 : 3;

    // fixed columns
    for (let c = 0; c < fixed; c++) {
      merges.push({ s: { r: 0, c }, e: { r: 2, c } });
    }

    // กลุ่มพัฒนาฝีมือ (9)
    const grpStart = fixed;
    merges.push({ s: { r: 0, c: grpStart }, e: { r: 0, c: grpStart + 8 } });

    merges.push({ s: { r: 1, c: grpStart + 0 }, e: { r: 2, c: grpStart + 0 } });
    merges.push({ s: { r: 1, c: grpStart + 1 }, e: { r: 2, c: grpStart + 1 } });
    merges.push({ s: { r: 1, c: grpStart + 2 }, e: { r: 2, c: grpStart + 2 } });
    merges.push({ s: { r: 1, c: grpStart + 3 }, e: { r: 2, c: grpStart + 3 } });
    merges.push({ s: { r: 1, c: grpStart + 4 }, e: { r: 2, c: grpStart + 4 } });

    merges.push({ s: { r: 1, c: grpStart + 5 }, e: { r: 1, c: grpStart + 6 } });
    merges.push({ s: { r: 1, c: grpStart + 7 }, e: { r: 1, c: grpStart + 8 } });

    // กลุ่มเงิน (3)
    const moneyStart = grpStart + 9;
    merges.push({ s: { r: 0, c: moneyStart }, e: { r: 0, c: moneyStart + 2 } });
    merges.push({ s: { r: 1, c: moneyStart + 0 }, e: { r: 2, c: moneyStart + 0 } });
    merges.push({ s: { r: 1, c: moneyStart + 1 }, e: { r: 2, c: moneyStart + 1 } });
    merges.push({ s: { r: 1, c: moneyStart + 2 }, e: { r: 2, c: moneyStart + 2 } });

    // วันที่จ่าย
    const payCol = moneyStart + 3;
    merges.push({ s: { r: 0, c: payCol }, e: { r: 2, c: payCol } });


    /** ================================
     * 3) Data rows
     * ================================= */
    items.forEach((r, idx) => {
      const row = [];

      row.push(
        idx + 1,
        r.SupportNo || "-",
        r.SupportName || "-"
      );

      if (showDeptCol) row.push(r.DeptName || "-");

      row.push(
        r.ReceiveYear || "-",
        r.ReceiptDate || "-",
        r.AvgMan,
        r.SpkTrainMan,
        Number(r.TrainPercent || 0).toFixed(2),
        r.Avg70,
        r.Avg80,
        r.CntGt70Lt80,
        r.CntGe80,
        r.Amt70,
        fmtMoney(r.Amt80),
        r.PaymentAmount,
        r.PaymentDate
      );

      aoa.push(row);
    });

    return { aoa, merges };
  };


  /** ===========================
   *   EXPORT EXCEL FUNCTION
   * =========================== */
  const handleExportExcel = () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }

    const { aoa, merges } = buildAoaAndMerges();

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!merges"] = merges;

    // auto width
    ws["!cols"] = aoa[0].map(() => ({ wch: 20 }));

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-2");

    XLSX.writeFile(
      wb,
      `รายงานผลการพิจารณา ชอ4-2.xlsx`
    );
  };


  const handleExportPDF = async () => {
    if (!items.length) { Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning"); return; }
    await ensureThaiFontLoaded();

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-2`, 40, 40);

    const head = [
      headerRow1.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      headerRow2.map(h => (typeof h === "string" ? { content: h } : { content: h.content, colSpan: h.colSpan, rowSpan: h.rowSpan, styles: { halign: "center", valign: "middle" } })),
      headerRow3.map(t => ({ content: t }))
    ];

    const body = items.map((r, idx) => {
      const row = [];
      row[COL.No] = idx + 1;
      row[COL.SupportNo] = r.SupportNo || "-";
      row[COL.SupportName] = r.SupportName || "-";
      if (showDeptCol) row[COL.DeptName] = r.DeptName || "-";

      row[COL.Y] = r.ReceiveYear || "-";
      row[COL.RDate] = r.ReceiptDate || "-";

      row[COL.Avg] = r.AvgMan;
      row[COL.Spk] = r.SpkTrainMan;
      row[COL.Pct] = r.TrainPercent ? Number(r.TrainPercent).toFixed(2) : "0.00";
      row[COL.Avg70] = r.Avg70;
      row[COL.Avg80] = r.Avg80;
      row[COL.Cnt7080] = r.CntGt70Lt80;
      row[COL.Cnt80] = r.CntGe80;

      row[COL.Amt70] = fmtMoney(r.Amt70);
      row[COL.Amt80] = fmtMoney(r.Amt80);
      row[COL.Total] = r.PaymentAmount ?? 0;
      row[COL.PayDate] = toThaiDateLabel(r.PaymentDate);

      for (let c = 0; c <= COL.PayDate; c++) if (row[c] === undefined) row[c] = "";
      return row;
    });

    // จัด index สำหรับจัดแนว
    const COL_IDX = COL;

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        [COL_IDX.SupportName]: { halign: "left" },
        ...(showDeptCol ? { [COL_IDX.DeptName]: { halign: "left" } } : {}),
        [COL_IDX.RDate]: { halign: "center" },
        [COL_IDX.Avg]: { halign: "right" },
        [COL_IDX.Spk]: { halign: "right" },
        [COL_IDX.Pct]: { halign: "right" },
        [COL_IDX.Avg70]: { halign: "right" },
        [COL_IDX.Avg80]: { halign: "right" },
        [COL_IDX.Cnt7080]: { halign: "right" },
        [COL_IDX.Cnt80]: { halign: "right" },
        [COL_IDX.Amt70]: { halign: "right" },
        [COL_IDX.Amt80]: { halign: "right" },
        [COL_IDX.Total]: { halign: "right" },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    // footer total
    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(`รวมจำนวนเงินช่วยเหลือ: ${fmtMoney(totalPayment)} บาท`, 40, doc.lastAutoTable.finalY + 24);

    const filename = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-2.pdf`;
    doc.save(filename);
  };

  /* ===== render ===== */
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={opt => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`); setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else { setReportMonth(""); setReportMonthDate(null); }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={<input className="form-control rounded-pill text-center input-color" readOnly value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />}
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={3} className="text-center align-middle">ที่</th>
              <th rowSpan={3} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={3} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}
              <th colSpan={9} className="text-center align-middle">
                ผู้ประกอบกิจการดำเนินการพัฒนาฝีมือแรงงานให้แก่ลูกจ้างในปีที่ผ่านมา
              </th>
              <th colSpan={3} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th rowSpan={2} className="text-center">ปี พ.ศ. (ระบุ)</th>
              <th rowSpan={2} className="text-center">วัน/เดือน/ปี ที่ยื่นแบบแสดงการส่งเงินสมทบ</th>

              <th rowSpan={2} className="text-center">จำนวนลูกจ้างเฉลี่ย (คน)</th>
              <th rowSpan={2} className="text-center">จำนวนลูกจ้างที่ได้รับการพัฒนาฯ (คน)</th>
              <th rowSpan={2} className="text-center">ร้อยละของจำนวนลูกจ้างที่ได้รับการพัฒนาฯ</th>
              <th colSpan={2} className="text-center">จำนวนลูกจ้างเฉลี่ย</th>
              <th colSpan={2} className="text-center">จำนวนลูกจ้างที่ได้รับการพัฒนาฯ</th>
              <th rowSpan={2} className="text-center">ส่วนที่เกินกว่า ร้อยละ 70 (บาท)</th>
              <th rowSpan={2} className="text-center">ส่วนที่เกินกว่า ร้อยละ 80 (บาท)</th>
              <th rowSpan={2} className="text-center">จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)</th>
            </tr>
            <tr>
              <th className="text-center">ร้อยละ 70 (คน)</th>
              <th className="text-center">ร้อยละ 80 (คน)</th>
              <th className="text-center">ส่วนที่เกินกว่า ร้อยละ 70 (คน)</th>
              <th className="text-center">ส่วนที่เกินกว่า ร้อยละ 80 (คน)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDeptCol ? 16 : 15} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {items.map((r, idx) => (
              <tr key={`${r.SupportNo}-${idx}`}>
                <td className="text-center">{idx + 1}</td>
                <td className="text-center">{r.SupportNo || "-"}</td>
                <td className="text-start">{r.SupportName || "-"}</td>
                {showDeptCol && <td className="text-start">{r.DeptName || "-"}</td>}

                <td className="text-center">{r.ReceiveYear || "-"}</td>
                <td className="text-center">{r.ReceiptDate || "-"}</td>

                <td className="text-end">{r.AvgMan}</td>
                <td className="text-end">{r.SpkTrainMan}</td>
                <td className="text-end">{Number(r.TrainPercent || 0).toFixed(2)}</td>
                <td className="text-end">{r.Avg70}</td>
                <td className="text-end">{r.Avg80}</td>
                <td className="text-end">{r.CntGt70Lt80}</td>
                <td className="text-end">{r.CntGe80}</td>

                <td className="text-end">{fmtMoney(r.Amt70)}</td>
                <td className="text-end">{fmtMoney(r.Amt80)}</td>
                <td className="text-end">{r.PaymentAmount}</td>

                <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>
              </tr>
            ))}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 15 : 14} className="text-end fw-bold">รวมจำนวนเงินช่วยเหลือ</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
